/**
 * (c) 2021, Micro:bit Educational Foundation and contributors
 *
 * SPDX-License-Identifier: MIT
 */
import {
  Button,
  Code,
  Kbd,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Text,
} from "@microbit/ui";
import { ReactNode } from "react";
import { FormattedMessage } from "react-intl";
import { VStack } from "styled-system/jsx";

interface SerialHelpDialogProps {
  isOpen: boolean;
  onClose: () => void;
  finalFocusRef?: React.RefObject<HTMLButtonElement | null>;
}

const formatValues = {
  code: (chunks: ReactNode) => <Code>{chunks}</Code>,
  kbd: (chunks: ReactNode) => <Kbd>{chunks}</Kbd>,
};

/**
 * Serial help triggered from the info icon.
 */
export const SerialHelpDialog = ({
  isOpen,
  onClose,
  finalFocusRef = undefined,
}: SerialHelpDialogProps) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      finalFocusRef={finalFocusRef}
    >
      <ModalHeader css={{ fontSize: "lg", fontWeight: "bold" }}>
        <FormattedMessage id="serial-help-title" />
      </ModalHeader>
      <ModalBody>
        <VStack gap="5" alignItems="stretch">
          <Text>
            <FormattedMessage id="serial-help-intro" />
          </Text>
          <Text>
            <FormattedMessage id="serial-help-print" values={formatValues} />
          </Text>
          <Text>
            <FormattedMessage id="serial-help-ctrl-c" values={formatValues} />
          </Text>
          <Text>
            <FormattedMessage id="serial-help-ctrl-d" values={formatValues} />
          </Text>
        </VStack>
      </ModalBody>
      <ModalFooter>
        <Button variant="primary" onPress={onClose}>
          <FormattedMessage id="close-action" />
        </Button>
      </ModalFooter>
    </Modal>
  );
};
